<script setup lang="ts">
const localeRoute = useLocaleRoute();
const route = useRoute();
const { user } = useAuth();

const accountNavigation = computed(() => [
  {
    key: "profile",
    label: "account_ui.profile_information",
    to: localeRoute("/profile"),
    prefix: "/profile",
  },
  {
    key: "settings",
    label: "account_ui.account_settings",
    to: localeRoute("/settings"),
    prefix: "/settings",
    exclude: ["/settings/notifications"],
  },
  {
    key: "notifications",
    label: "settings_page.notifications.title",
    to: localeRoute("/settings/notifications"),
    prefix: "/settings/notifications",
  },
]);

const workerNavigation = computed(() => {
  if (user.value?.role !== "worker") return [];

  return [
    {
      key: "worker-profile",
      label: "account_ui.professional",
      to: localeRoute("/worker/profile"),
      prefix: "/worker/profile",
      exclude: [
        "/worker/profile/services",
        "/worker/profile/experience",
        "/worker/profile/education",
      ],
    },
    {
      key: "worker-services",
      label: "worker_profile_page.services.title",
      to: localeRoute("/worker/profile/services"),
      prefix: "/worker/profile/services",
    },
    {
      key: "worker-experience",
      label: "worker_profile_page.experience.title",
      to: localeRoute("/worker/profile/experience"),
      prefix: "/worker/profile/experience",
    },
    {
      key: "worker-education",
      label: "worker_profile_page.education.title",
      to: localeRoute("/worker/profile/education"),
      prefix: "/worker/profile/education",
    },
  ];
});

const mobileNavigation = computed(() => [
  ...accountNavigation.value,
  ...workerNavigation.value,
]);

function normalizedPath(value: string) {
  const localePrefix = route.path.match(/^\/(lt|en|ru)(?=\/|$)/)?.[0] || "";
  return value.replace(localePrefix, "") || "/";
}

function targetPath(to: any) {
  const value = typeof to === "string" ? to : to?.path;
  return value ? normalizedPath(value) : "";
}

function isNavigationActive(item: any) {
  const current = normalizedPath(route.path);
  const target = targetPath(item.to);

  if (item.exclude?.some((value: string) => current.startsWith(value))) {
    return false;
  }

  const prefix = item.prefix || target;
  return current === prefix || current.startsWith(`${prefix}/`);
}
</script>

<template>
  <div class="flex min-h-screen flex-col bg-white">
    <Header />

    <main class="flex-1 bg-white">
      <div
        class="mx-auto w-full max-w-[1280px] px-4 pb-16 pt-0 sm:px-6 lg:grid lg:grid-cols-[250px_minmax(0,860px)] lg:justify-between lg:gap-16 lg:pb-24 lg:pt-8"
      >
        <aside class="min-w-0 lg:sticky lg:top-8 lg:self-start">
          <div class="hidden lg:block">
            <h2
              class="font-gabarito text-[28px] font-bold leading-none tracking-[-0.025em] text-app-text"
            >
              {{ $t("account_navigation.settings") }}
            </h2>

            <nav class="mt-6 text-[16px] leading-6">
              <div class="space-y-3">
                <NuxtLink
                  v-for="item in accountNavigation"
                  :key="item.key"
                  :to="item.to"
                  class="block py-2.5 transition-colors"
                  :class="
                    isNavigationActive(item)
                      ? 'font-semibold text-app-text'
                      : 'font-normal text-[#46504B] hover:text-primary'
                  "
                >
                  {{ $t(item.label) }}
                </NuxtLink>

                <NuxtLink
                  v-for="item in workerNavigation"
                  :key="item.key"
                  :to="item.to"
                  class="block py-2.5 transition-colors"
                  :class="
                    isNavigationActive(item)
                      ? 'font-semibold text-app-text'
                      : 'font-normal text-[#46504B] hover:text-primary'
                  "
                >
                  {{ $t(item.label) }}
                </NuxtLink>
              </div>
            </nav>
          </div>

          <nav class="-mx-4 mb-0 border-b border-app-border sm:-mx-6 lg:hidden">
            <div
              class="w-full scroll-px-4 overflow-x-auto overscroll-x-contain px-4 [scrollbar-width:none] sm:scroll-px-6 sm:px-6 [&::-webkit-scrollbar]:hidden"
            >
              <div class="flex min-w-max gap-6">
                <NuxtLink
                  v-for="item in mobileNavigation"
                  :key="item.key"
                  :to="item.to"
                  class="relative inline-flex shrink-0 items-center whitespace-nowrap py-3.5 text-sm font-medium transition-colors"
                  :class="
                    isNavigationActive(item)
                      ? 'font-semibold text-app-text after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:bg-primary'
                      : 'text-app-muted hover:text-app-text'
                  "
                >
                  {{ $t(item.label) }}
                </NuxtLink>
              </div>
            </div>
          </nav>
        </aside>

        <div class="min-h-[calc(100svh-180px)] min-w-0 w-full pt-8 lg:pt-0">
          <slot />
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>
